import React, { useState, ReactElement, useEffect, useMemo } from 'react';
import { ApolloProvider, ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import { Button, LoadingSpinner, Icon, colors } from 'shared-library';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import styled from 'styled-components';
import { getHiApiServerlessUrl } from 'packages/locations';
import { buildStudySelector } from '../../store/studies/selectors';
import { selectSession } from '../../store/session/selectors';
import { useConfirmBodyCompQC } from '../../hooks/useConfirmBodyCompQC';
import { addLogItem } from '../../store/studies/actions';

const SKU_ID = {
  BODY_COMP: '370',
  EXECUTIVE_PKG: '900',
};

const USER_ROLES = {
  REPORT_QC: 'report_qc',
};

const MESSAGES = {
  SUCCESS: 'Confirmed successfully!',
  ERROR: 'Failed to confirm Body Composition QC.',
  LOG_ERROR: 'PDF was uploaded successfully but adding study log failed.',
};

const TOAST_CONFIG = {
  appearance: 'info',
  autoDismiss: true,
  autoDismissTimeout: 10000,
  placement: 'bottom-left',
};

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  color: ${colors.success};
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  color: ${colors.error};
  margin-left: 8px;
`;

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const hasBodyCompService = (study): boolean => {
  return (
    study?.poetryMetadata?.servicesInfo?.some((service) => {
      return service.code === 'IMAGING.BASE.BODY_COMPOSITION';
    }) ?? false
  );
};

const BodyCompQC = (): ReactElement => {
  const { studyId } = useParams<{ studyId: string }>();
  const study = useSelector(buildStudySelector(studyId));
  const { data: session } = useSelector(selectSession);
  const { addToast } = useToasts();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const { confirmBodyCompQC, error } = useConfirmBodyCompQC(studyId);

  // Display error toast if query fails
  useEffect(() => {
    if (error) {
      const message = error.message || MESSAGES.ERROR;
      addToast(message, { ...TOAST_CONFIG, appearance: 'error' });
      setErrorMessage(message);
    }
  }, [error, addToast]);

  // Show for SKU 370 (Body Comp) or SKU 900 (Executive PKG) with BodyComp service, and users with report_qc role
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const primarySkuId = study?.items?.primary?.skuID;
    const hasRequiredRole = session?.roles?.includes(USER_ROLES.REPORT_QC);

    const hasValidSku =
      primarySkuId === SKU_ID.BODY_COMP ||
      (primarySkuId === SKU_ID.EXECUTIVE_PKG && hasBodyCompService(study));

    setShouldRender(hasValidSku && hasRequiredRole);
  }, [study?.items?.primary?.skuID, study?.poetryMetadata?.servicesInfo, session?.roles]);

  if (!shouldRender) {
    return null;
  }

  const handleConfirmQC = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const { data } = await confirmBodyCompQC();
      const statusCode = data?.confirmBodyCompQC?.statusCode;
      const message = data?.confirmBodyCompQC?.message || MESSAGES.ERROR;

      if (statusCode >= 200 && statusCode < 300) {
        try {
          await addLogItem(studyId, 'Body Composition QC confirmed')(dispatch);
          addToast(MESSAGES.SUCCESS, { ...TOAST_CONFIG, appearance: 'success' });
          setIsSuccess(true);
        } catch (logError) {
          addToast(MESSAGES.LOG_ERROR, { ...TOAST_CONFIG, appearance: 'warning' });
          setErrorMessage(MESSAGES.LOG_ERROR);
        }
      } else {
        addToast(message, { ...TOAST_CONFIG, appearance: 'error' });
        setErrorMessage(message);
      }
    } catch (err) {
      addToast(MESSAGES.ERROR, { ...TOAST_CONFIG, appearance: 'error' });
      setErrorMessage(MESSAGES.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <Container>
        <SuccessMessage>
          <Icon
            type="check-circle"
            color={colors.success}
            size="xs"
            style={{ marginRight: '8px' }}
          />
          <span>{MESSAGES.SUCCESS}</span>
        </SuccessMessage>
      </Container>
    );
  }

  return (
    <Container>
      {isLoading ? (
        <LoadingSpinner size="small" />
      ) : (
        <Button onClick={handleConfirmQC} variant="primary" disabled={isLoading} size="small">
          Confirm QC
        </Button>
      )}
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </Container>
  );
};

const BodyCompQCWithApollo = (): ReactElement => {
  const [apiEndpoint] = useState(getHiApiServerlessUrl());
  const createApolloClient = (endpoint: string) => {
    return new ApolloClient({
      link: new HttpLink({
        uri: endpoint,
        credentials: 'include',
      }),
      cache: new InMemoryCache(),
    });
  };

  const apolloClient = useMemo(() => createApolloClient(apiEndpoint), [apiEndpoint]);

  if (!apiEndpoint) {
    return null;
  }

  return (
    <ApolloProvider client={apolloClient}>
      <BodyCompQC />
    </ApolloProvider>
  );
};

export default BodyCompQCWithApollo;
